import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { FiStar, FiCheck, FiAlertCircle, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { ouvidoriaPortalService } from '../../services/ouvidoria.service';

interface Estab { nome: string; logo_url: string | null; cor_primaria: string | null; }
interface DocResumo { numero: string; titulo: string; status: string; }
interface AvaliacaoExistente { estrelas: number | null; comentario: string | null; resolveu: boolean | null; avaliado_em: string | null; }

export function OuvidoriaAvaliarPage() {
  const { token } = useParams<{ token: string }>();
  const [estab, setEstab] = useState<Estab | null>(null);
  const [doc, setDoc] = useState<DocResumo | null>(null);
  const [existente, setExistente] = useState<AvaliacaoExistente | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [estrelas, setEstrelas] = useState(0);
  const [hoverEstrelas, setHoverEstrelas] = useState(0);
  const [resolveu, setResolveu] = useState<boolean | null>(null);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    if (!token) return;
    ouvidoriaPortalService.lerAvaliacao(token)
      .then((d) => {
        const dados = d as { erro?: string; documento?: DocResumo; estabelecimento?: Estab; avaliacao?: AvaliacaoExistente };
        if (dados.erro) {
          setErro(dados.erro);
        } else {
          setDoc(dados.documento ?? null);
          setEstab(dados.estabelecimento ?? null);
          if (dados.avaliacao?.avaliado_em) {
            setExistente(dados.avaliacao);
          }
        }
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (estrelas === 0) { setErro('Selecione de 1 a 5 estrelas.'); return; }
    setErro(null);
    setEnviando(true);
    try {
      await ouvidoriaPortalService.enviarAvaliacao(token, {
        estrelas,
        comentario: comentario.trim() || undefined,
        resolveu: resolveu ?? undefined,
      });
      setEnviado(true);
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao enviar.');
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <PortalPage titulo="Avaliar atendimento">
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  if (erro && !doc) {
    return (
      <PainelResultado
        tom="aviso"
        icone={<FiAlertCircle size={32} />}
        titulo="Não foi possível abrir esta avaliação"
        descricao={erro === 'token_invalido' ? 'O link é inválido ou expirou.' : erro}
      />
    );
  }

  const jaAvaliado = !!existente || enviado;

  if (jaAvaliado) {
    return (
      <PainelResultado
        titulo="Obrigado!"
        descricao={enviado ? 'Sua avaliação foi registrada.' : 'Esta manifestação já foi avaliada.'}
      >
        {existente?.estrelas != null && (
          <div className="flex items-center justify-center gap-2 text-sm">
            Nota: {[1, 2, 3, 4, 5].map((n) => (
              <FiStar
                key={n}
                size={18}
                className={n <= (existente.estrelas ?? 0) ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}
              />
            ))}
          </div>
        )}
      </PainelResultado>
    );
  }

  return (
    <PortalPage
      titulo="Avaliar atendimento"
      subtitulo={estab?.nome ?? undefined}
      icone={<FiStar size={26} />}
      largura="media"
    >
      <SectionCard titulo="Sua avaliação" subtitulo={doc?.numero} icone={<FiStar size={18} />}>
        <div className="mb-6">
          <div className="text-xs text-slate-500">Manifestação</div>
          <div className="text-base text-slate-900 mt-1">{doc?.titulo}</div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {erro && <Alert variante="erro">{erro}</Alert>}

          <div>
            <Label obrigatorio>Como você avalia o atendimento?</Label>
            <div className="flex items-center gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setEstrelas(n)}
                  onMouseEnter={() => setHoverEstrelas(n)}
                  onMouseLeave={() => setHoverEstrelas(0)}
                  className="p-1 transition"
                  aria-label={`${n} estrela(s)`}
                >
                  <FiStar
                    size={32}
                    className={
                      n <= (hoverEstrelas || estrelas)
                        ? 'text-amber-500 fill-amber-500'
                        : 'text-slate-300 hover:text-amber-300'
                    }
                  />
                </button>
              ))}
              {estrelas > 0 && (
                <span className="ml-3 text-sm font-medium text-slate-700">
                  {['', 'Muito ruim', 'Ruim', 'Regular', 'Bom', 'Excelente'][estrelas]}
                </span>
              )}
            </div>
          </div>

          <div>
            <Label>Sua manifestação foi resolvida?</Label>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setResolveu(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded border-2 transition ${
                  resolveu === true
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 text-slate-700 hover:border-slate-300'
                }`}
              >
                <FiThumbsUp size={14} /> Sim
              </button>
              <button
                type="button"
                onClick={() => setResolveu(false)}
                className={`flex items-center gap-2 px-4 py-2 rounded border-2 transition ${
                  resolveu === false
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-slate-200 text-slate-700 hover:border-slate-300'
                }`}
              >
                <FiThumbsDown size={14} /> Não
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="com">Comentário (opcional)</Label>
            <textarea
              id="com"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              rows={5}
              placeholder="Conte-nos como foi o atendimento, o que pode melhorar, etc."
              className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" loading={enviando} disabled={estrelas === 0}>
              <FiCheck size={14} /> Enviar avaliação
            </Button>
          </div>

          <p className="text-xs text-slate-500 text-center">
            Sua avaliação ajuda a prefeitura a melhorar continuamente os serviços prestados ao cidadão.
          </p>
        </form>
      </SectionCard>
    </PortalPage>
  );
}
